import { useQuery } from "@tanstack/react-query";
import { ApiClientError } from "../services/api-client";
import { getCoachingDataMode, getCoachingZ6Services } from "../services/coaching-service-provider";
import type {
  CoachingAssignmentLogDto,
  CoachingAttendanceLogDto,
  CoachingInviteLogDto,
  CoachingSessionRecordingsDto,
  CoachingUseCaseListDto,
  CoachingWeekWiseProgressDto,
  PostSessionEmailLogDto
} from "../services/coaching-z6-dtos";
import { liveProductQueryPolicy } from "./useLiveProductData";
import { useWalkReadinessHold } from "../lib/walk-readiness";
import { coachingQueryKeys } from "./coaching-query-keys";
/**
 * Z6 read bindings. Same honest posture as useLiveProductData: live mode renders
 * backend rows; mock/evidence mode stays disabled (isLiveBound false) so the
 * deterministic fixtures back the e2e suite without network traffic. A 403
 * surfaces as `denied`; any other backend failure (including an absent Z-route
 * before deploy) surfaces as `isError`, never a crash or widened access. The
 * loaded data object is returned verbatim so the typed gated states stay intact.
 */
export interface CoachingZ6Binding<TData> {
  isLiveBound: boolean;
  isLoading: boolean;
  isError: boolean;
  denied: boolean;
  reference?: string;
  data: TData | null;
}

function isLive() {
  return getCoachingDataMode() === "live";
}

function bindingStatus(enabled: boolean, isLoading: boolean, error: unknown) {
  const denied = enabled && error instanceof ApiClientError && error.status === 403;
  return {
    isLiveBound: enabled,
    isLoading: enabled && isLoading,
    isError: enabled && Boolean(error) && !denied,
    denied,
    reference: error instanceof ApiClientError ? (error.reference ?? undefined) : undefined
  };
}

function useZ6Query<TData>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<TData>,
  recordId: string | null,
  enabledOverride = true
): CoachingZ6Binding<TData> {
  const enabled = isLive() && Boolean(recordId) && enabledOverride;
  const query = useQuery({ 
    queryKey, 
    queryFn, 
    enabled, 
    ...liveProductQueryPolicy,
    staleTime: 30000,
  });
  useWalkReadinessHold(enabled && query.isLoading);
  
  // Treat long-loading as error (latency window fallback)
  const isStuckLoading = enabled && query.isLoading && (query.dataUpdatedAt === 0);
  return {
    ...bindingStatus(enabled, query.isLoading && !isStuckLoading, query.error || (isStuckLoading ? new Error("z6-query-timeout") : null)),
    data: (enabled && query.data) || null
  };
}

export function useCoachingRecordings(sessionId: string | null) {
  return useZ6Query<CoachingSessionRecordingsDto>(
    coachingQueryKeys.z6.recordings(sessionId ?? "missing"),
    () => getCoachingZ6Services().recordings.forSession(sessionId ?? ""),
    sessionId
  );
}

export function useCoachingInviteLog(sessionId: string | null) {
  return useZ6Query<CoachingInviteLogDto>(
    coachingQueryKeys.z6.inviteLog(sessionId ?? "missing"),
    () => getCoachingZ6Services().inviteLog.forSession(sessionId ?? ""),
    sessionId
  );
}

export function useCoachingPostSessionEmailLog(sessionId: string | null) {
  return useZ6Query<PostSessionEmailLogDto>(
    coachingQueryKeys.z6.postSessionEmailLog(sessionId ?? "missing"),
    () => getCoachingZ6Services().postSessionEmail.log(sessionId ?? ""),
    sessionId
  );
}

export function useCoachingWeekWiseProgress(cohortId: string | null) {
  return useZ6Query<CoachingWeekWiseProgressDto>(
    coachingQueryKeys.z6.progressWeekWise(cohortId ?? "missing"),
    () => getCoachingZ6Services().progress.weekWise(cohortId ?? ""),
    cohortId
  );
}

export function useCoachingAttendance(cohortId: string | null) {
  return useZ6Query<CoachingAttendanceLogDto>(
    coachingQueryKeys.z6.attendance(cohortId ?? "missing"),
    () => getCoachingZ6Services().attendance.forCohort(cohortId ?? ""),
    cohortId
  );
}

export function useCoachingAssignments(cohortId: string | null) {
  return useZ6Query<CoachingAssignmentLogDto>(
    coachingQueryKeys.z6.assignments(cohortId ?? "missing"),
    () => getCoachingZ6Services().assignments.forCohort(cohortId ?? ""),
    cohortId
  );
}

export function useCoachingUseCases(enabled = true) {
  // Use-cases are org-scoped (no per-record id); a sentinel keeps the shared
  // enablement gate happy while still respecting live-mode and the caller flag.
  return useZ6Query<CoachingUseCaseListDto>(
    coachingQueryKeys.z6.useCases(),
    () => getCoachingZ6Services().useCases.list(),
    "use-cases",
    enabled
  );
}
